import {
  CcfAlert,
  CcfBox,
  CcfButton,
  CcfCalendarIcon,
  CcfSnackBar,
  CcfTextField,
  CcfTypography,
  CcfUpArrowIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './ccf-schedule-new-commitment.module.scss';
import {
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Theme,
} from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setNewCommitment } from '../ccf-inbox/ccf-inbox.slice';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { makeStyles, createStyles } from '@mui/styles';

const useStyles = makeStyles((theme) =>
  createStyles({
    inputRoot: {
      font: 'normal normal normal 13px/30px Open Sans !important',
      letterSpacing: '0px',
      color: '#33333 !important',
      lineHeight: '18px',
      '&. MuiInputBase-root': {
        font: 'normal normal normal 13px/30px Open Sans !important',
        letterSpacing: '0px',
        webkitTextFillColor: '#333333 !important',
        lineHeight: '18px',
      },
    },
    input: {
      color: '#333333 !important',
    },
    disabled: {
      '&.Mui-disabled': {
        '-webkit-text-fill-color': '#333333 !important',
      },
    },
  })
);

/* eslint-disable-next-line */
export interface CcfScheduleNewCommitmentProps {}

/**
 * used to display new Schedule commitment form
 * @param props - CcfScheduleNewCommitmentProps
 * @example ` <CcfScheduleNewCommitment />`
 */
export function CcfScheduleNewCommitment(props: CcfScheduleNewCommitmentProps) {
  const dispatch = useDispatch();
  const skills = ['Spanish Verbal', 'English Verbal', 'Facebook DFO'];
  const [selectedRadioButton, setSelectedRadioButton] = useState('Agent');
  const [selectedDropdownValue, setSelectedDropdownValue] =
    useState('Spanish Verbal');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSaveBtnClicked, setIsSaveBtnClicked] = useState(false);
  const [isCommitmentSaved, setIsCommitmentSaved] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const classes = useStyles();
  const [translate] = useTranslator();

  /**
   * Updating Radio Button Change
   * @example - handleRadioChange()
   */
  const handleRadioChange = (value: string) => {
    setSelectedRadioButton(value);
    setSelectedDropdownValue('Spanish Verbal');
  };

  /**
   * function to close new commitment section
   * @example - closeNewCommitmentHandler()
   */
  const closeNewCommitmentHandler = (action: string) => {
    if (action === 'Save') {
      setIsSaveBtnClicked(true); //Save Btn is clicked
      if (firstName && lastName && phoneNumber) {
        setIsCommitmentSaved(true);
        setTimeout(() => {
          dispatch(setNewCommitment(false));
        }, 3000);
      }
    } else {
      dispatch(setNewCommitment(false)); //close New Commitment View
    }
  };

  /**
   * Used to close the snackbar
   * @param event -React.SyntheticEvent
   * @param reason -string
   * @example - handleClose()
   */
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsCommitmentSaved(false);
  };

  return (
    <>
      <CcfBox className={styles.headerSection}>
        <CcfTypography
          className={styles.backBtn}
          onClick={() => closeNewCommitmentHandler('Go back')}
        >
          <KeyboardArrowLeftIcon fontSize="small" />
          {translate('goBack')}
        </CcfTypography>
      </CcfBox>
      <Divider />
      <CcfBox className={styles.newCommitmentContainer}>
        <CcfBox>
          <CcfTypography className={styles.addCommitmentTxt}>
          {translate('addCommitment')}
          </CcfTypography>
        </CcfBox>
        <CcfBox mb={2}>
          <CcfBox>
            <FormControl>
              <RadioGroup row>
                <FormControlLabel
                  value="Agent"
                  control={<Radio />}
                  label={
                    <CcfTypography className={styles.radioLabel}>
                      Agent (Me)
                    </CcfTypography>
                  }
                  checked={selectedRadioButton === 'Agent'}
                  onChange={() => handleRadioChange('Agent')}
                />
                <FormControlLabel
                  value="Skill"
                  control={<Radio />}
                  label={
                    <CcfTypography className={styles.radioLabel}>
                      {translate('agent')}
                    </CcfTypography>
                  }
                  onChange={() => handleRadioChange('Skill')}
                />
              </RadioGroup>
            </FormControl>
          </CcfBox>
          <CcfBox>
            <Select
              displayEmpty
              name="skill"
              variant="outlined"
              className={
                selectedRadioButton === 'Skill'
                  ? styles.dropdown
                  : styles.disabledDropdown
              }
              SelectDisplayProps={{
                style: {
                  fontSize: '12px',
                  color: '#333333',
                  borderColor: '#c4c4c4',
                },
              }}
              IconComponent={() => (
                <CcfUpArrowIcon className={styles.arrowIconForDropdown} />
              )}
              inputProps={{
                classes: {
                  root: classes.inputRoot,
                  input: classes.input,
                  disabled: classes.disabled,
                },
              }}
              value={selectedDropdownValue}
              onChange={(e: any) => {
                setSelectedDropdownValue(
                  (e?.target as HTMLSelectElement).value
                );
              }}
              disabled={selectedRadioButton === 'Agent'}
            >
              {skills.map((item: any) => {
                return <MenuItem value={item}>{item}</MenuItem>;
              })}
            </Select>
          </CcfBox>
        </CcfBox>
        <Divider />
        <CcfBox mb={1} mt={1}>
          <CcfTypography className={styles.title}>Customer</CcfTypography>
          <CcfBox style={{ display: 'flex', width: '100%' }} mt={1}>
            <CcfBox style={{ width: '45%' }}>
              <CcfTypography className={styles.label}>
                First Name *
              </CcfTypography>
              <CcfTextField
                id="firstName"
                variant="outlined"
                size="small"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
                focused={false}
                InputProps={{
                  className:
                    firstName || !isSaveBtnClicked
                      ? styles.inputTxt
                      : styles.requiredError,
                }}
              />
            </CcfBox>
            <CcfBox style={{ width: '10%' }}></CcfBox>
            <CcfBox style={{ width: '45%' }}>
              <CcfTypography className={styles.label}>
                Last Name *
              </CcfTypography>
              <CcfTextField
                id="lastName"
                variant="outlined"
                size="small"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
                focused={false}
                InputProps={{
                  className:
                    lastName || !isSaveBtnClicked
                      ? styles.inputTxt
                      : styles.requiredError,
                }}
              />
            </CcfBox>
          </CcfBox>
          <CcfBox mt={2}>
            <CcfTypography className={styles.label}>
              Contact Information *
            </CcfTypography>
            <CcfBox style={{ display: 'flex' }}>
              <CcfBox className={styles.labelBtn}>
                <CcfTypography className={styles.btnTxt}>Phone</CcfTypography>
              </CcfBox>
              <CcfTextField
                id="phoneNumber"
                fullWidth
                variant="outlined"
                size="small"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                }}
                focused={false}
                InputProps={{
                  className:
                    phoneNumber || !isSaveBtnClicked
                      ? styles.inputTxt
                      : styles.requiredError,
                }}
              />
            </CcfBox>
          </CcfBox>
        </CcfBox>
        <CcfBox mb={1}>
          <CcfTypography className={styles.title}>Time *</CcfTypography>
          <CcfBox mt={1}>
            <DatePicker
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              timeInputLabel="Time :"
              dateFormat="eeee, MMM d, yyyy h:mm aa"
              showTimeInput
              className={styles.datePickerBox}
              popperPlacement="top"
              onKeyDown={(e) => {
                e.preventDefault();
              }}
              onClickOutside={() => {
                setShowDatePicker(false);
              }}
              onInputClick={() => {
                setShowDatePicker(true);
              }}
              onSelect={() => {
                setShowDatePicker(false);
              }}
              open={showDatePicker}
            />
            <CcfCalendarIcon
              style={{ position: 'relative', left: '90%', bottom: '28px' }}
              onClick={() => {
                setShowDatePicker(true);
              }}
            />
          </CcfBox>
        </CcfBox>
        <CcfBox mb={1} mt={-3}>
          <CcfTypography className={styles.title}>Time Zone</CcfTypography>
          <CcfBox mt={1}>
            <CcfTextField
              id="StandardTime"
              variant="outlined"
              fullWidth
              size="small"
              value="UTC-07:00 Mountain Standard Time"
              focused={false}
              InputProps={{ className: styles.inputTxt }}
            />
          </CcfBox>
        </CcfBox>
        <CcfBox mb={2}>
          <CcfTypography className={styles.title}>Notes</CcfTypography>
          <CcfBox mt={1}>
            <CcfTextField
              id="notes"
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              maxRows={2}
              autoFocus={false}
              InputProps={{ className: styles.inputTxt }}
              focused={false}
              placeholder="Add notes"
            />
          </CcfBox>
        </CcfBox>
        <CcfBox className={styles.btnActions}>
          <CcfBox>
            <CcfTypography
              className={styles.deleteBtn}
              onClick={() => closeNewCommitmentHandler('Delete')}
            >
              Delete
            </CcfTypography>
          </CcfBox>
          <CcfBox className={styles.btnContainer}>
            <CcfButton
              className={`${styles.btn} ${styles.CancelButton}`}
              onClick={() => closeNewCommitmentHandler('Cancel')}
            >
              <CcfTypography
                className={`${styles.btnText} ${styles.txtCancel}`}
              >
                Cancel
              </CcfTypography>
            </CcfButton>
            <CcfButton
              className={`${styles.btn} ${styles.saveButton}`}
              onClick={() => closeNewCommitmentHandler('Save')}
            >
              <CcfTypography className={`${styles.btnText} ${styles.txtSave}`}>
                Save
              </CcfTypography>
            </CcfButton>
          </CcfBox>
        </CcfBox>
      </CcfBox>
      <CcfBox>
        <CcfSnackBar
          onClose={() => handleClose()}
          open={isCommitmentSaved}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <div>
            <CcfAlert closeAlert={handleClose} severity="success">
              Commitment Saved
            </CcfAlert>
          </div>
        </CcfSnackBar>
      </CcfBox>
    </>
  );
}

export default CcfScheduleNewCommitment;
